import { useState } from "react";
import { Space, Tag, Typography } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { formatDate } from "../utils";

// Shared cell renderers used by detail pages.
const yesNo = (v) =>
  v ? <Tag color="orange">Yes</Tag> : <Tag color="default">No</Tag>;

// R25 — super-admin-only portal password reveal. Masked by default (so the
// value is not exposed to shoulder-surfing the moment the page opens); the eye
// icon toggles reveal, and the copy icon copies the real value even while
// masked. Only super admins can see this at all: the password only reaches the
// page from the super_admin-gated client-detail endpoint (which also writes a
// `view_client_pw` audit row on every read), so lower roles never receive it.
function PasswordReveal({ value, unreadable }) {
  const [shown, setShown] = useState(false);
  if (!value) {
    // BE sets the unreadable flag when a password IS stored but its
    // ciphertext predates the current encryption key. Before this, the
    // whole detail endpoint 500'd on such rows ("Failed to load record").
    if (unreadable) {
      return (
        <Tag
          color="red"
          title="A password is saved for this client but it was encrypted with an old security key and cannot be read. Re-saving the client's password will fix it."
        >
          Unreadable (old key)
        </Tag>
      );
    }
    return "-";
  }
  return (
    <Space size={8}>
      <Typography.Text
        style={{ fontFamily: "monospace", letterSpacing: shown ? 0 : 2 }}
      >
        {shown ? value : "••••••••"}
      </Typography.Text>
      <Typography.Link
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "Hide password" : "Show password"}
        title={shown ? "Hide password" : "Show password"}
      >
        {shown ? <EyeInvisibleOutlined /> : <EyeOutlined />}
      </Typography.Link>
      <Typography.Text copyable={{ text: value }} />
    </Space>
  );
}

// Declarative render helpers for the portal-password fields on the client
// detail pages (masked-by-default reveal, super_admin only). The record's
// *_unreadable flags mark stored-but-undecryptable legacy ciphertext.
const passwordReveal = (v, record) => (
  <PasswordReveal value={v} unreadable={record?.password_unreadable} />
);
const tanPasswordReveal = (v, record) => (
  <PasswordReveal value={v} unreadable={record?.tan_password_unreadable} />
);

// Notice letter URL — shows the presigned S3 URL (copyable) + an
// "Open in new tab" link. The inline iframe preview was removed
// because S3 presigned URLs have a 7-day TTL — viewing an expired
// link rendered an AccessDenied/XML error inside the iframe.
const pdfPreview = (v) => {
  if (!v) return "-";
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs break-all">
      <Typography.Text copyable={{ text: v }}>{v}</Typography.Text>
      <a href={v} target="_blank" rel="noopener noreferrer">
        Open in new tab ↗
      </a>
    </div>
  );
};

const date = (v) => formatDate(v);
const dateTime = (v) => formatDate(v, true);

// Many-to-many assignees. Renders the `assigned_users` array (objects of
// {user_id, user_name, user_email}) as a row of tags; an empty array is
// the Shared pool, shown as a muted "Shared" tag.
const assignedUsers = (v) => {
  const list = Array.isArray(v) ? v : [];
  if (list.length === 0) return <Tag color="default">Shared</Tag>;
  return (
    <span className="flex flex-wrap gap-1">
      {list.map((u) => (
        <Tag color="blue" key={u.user_id}>
          {u.user_name || u.user_email || u.user_id}
        </Tag>
      ))}
    </span>
  );
};

const number = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("en-IN");

// Hide upstream-leaked timestamp / pure-numeric values that snuck into
// `response_status` / `notice_status`. Same regex set as the
// `_looksLikeJunkStatus` guard in columns.jsx.
const cleanStatus = (v) => {
  if (typeof v !== "string" || !v) return "-";
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return "-";
  if (/^\d{2}\/\d{2}\/\d{4}/.test(v)) return "-";
  if (/^[\d.\s:-]+$/.test(v.trim())) return "-";
  return v;
};

// ---------------------------------------------------------------------------
// CLIENTS
// ---------------------------------------------------------------------------

export const GST_CLIENT_DETAIL = {
  title: "GST Client",
  backLabel: "GST Clients",
  // R11: each count carries a `listPath` so the Statistic on the detail
  // page becomes a Link to the relevant notices list filtered by this
  // client. Falls back to plain (non-clickable) Statistic if listPath
  // is omitted.
  linkedCountsLabels: {
    notices: {
      label: "GST Notices & Orders",
      listPath: "/admin/gst/notices",
    },
    additional_notices: {
      label: "GST Additional Notices",
      listPath: "/admin/gst/additional-notices",
    },
  },
  fieldGroups: [
    {
      section: "Identity",
      fields: [
        { label: "Client Name", key: "client_name" },
        { label: "GSTIN", key: "gstin" },
        { label: "GST Username", key: "gst_username" },
        { label: "Portal Password", key: "password", render: passwordReveal },
        { label: "Portal Name of Assessee", key: "portal_name_of_assessee" },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
        { label: "Processing", key: "is_processing", render: yesNo },
        { label: "Last GST Sync", key: "last_gst_sync", render: dateTime },
        { label: "Last Execution", key: "last_execution_time", render: dateTime },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
        { label: "Assigned User", key: "assigned_users", render: assignedUsers },
        { label: "Partner Client Ref", key: "partner_client_ref" },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const IT_CLIENT_DETAIL = {
  title: "Income Tax Client",
  backLabel: "Income Tax Clients",
  linkedCountsLabels: {
    eproceedings: {
      label: "E-Proceedings",
      listPath: "/admin/income-tax/eproceedings",
    },
    response_outstanding_demands: {
      label: "Response to Outstanding Demands",
      listPath: "/admin/income-tax/response-outstanding-demands",
    },
  },
  fieldGroups: [
    {
      section: "Identity",
      fields: [
        { label: "Client Name", key: "client_name" },
        { label: "PAN / TAN (username)", key: "username" },
        { label: "Portal Password", key: "password", render: passwordReveal },
        { label: "Portal Name of Assessee", key: "portal_name_of_assessee" },
        { label: "Date of Birth", key: "dob", render: date },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
        { label: "Processing", key: "is_processing", render: yesNo },
        {
          label: "Last Income Tax Sync",
          key: "last_income_tax_sync",
          render: dateTime,
        },
        { label: "Last Execution", key: "last_execution_time", render: dateTime },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
        { label: "Assigned User", key: "assigned_users", render: assignedUsers },
        { label: "Partner Client Ref", key: "partner_client_ref" },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const TDS_CLIENT_DETAIL = {
  title: "TDS / TCS Client",
  backLabel: "TDS / TCS Clients",
  linkedCountsLabels: {
    notices: {
      label: "TDS / TCS Notices",
      listPath: "/admin/tds-tcs/notices",
    },
  },
  fieldGroups: [
    {
      section: "Identity",
      fields: [
        { label: "Client Name", key: "client_name" },
        { label: "TAN", key: "tan_number" },
        { label: "TDS Username", key: "tds_username" },
        { label: "Portal Password", key: "password", render: passwordReveal },
        { label: "TAN Portal Password", key: "tan_password", render: tanPasswordReveal },
        { label: "Portal Name of Assessee", key: "portal_name_of_assessee" },
        { label: "Date of Birth", key: "dob", render: date },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
        { label: "Processing", key: "is_processing", render: yesNo },
        { label: "Last TDS Sync", key: "last_tds_sync", render: dateTime },
        {
          label: "Last TDS-ITD Sync",
          key: "last_tds_itd_sync",
          render: dateTime,
        },
        { label: "Last Execution", key: "last_execution_time", render: dateTime },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
        { label: "Assigned User", key: "assigned_users", render: assignedUsers },
        { label: "Partner Client Ref", key: "partner_client_ref" },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// NOTICES
// ---------------------------------------------------------------------------

export const GST_NOTICE_DETAIL = {
  title: "GST Notice / Order",
  backLabel: "GST Notices & Orders",
  fieldGroups: [
    {
      section: "Identifiers",
      fields: [
        { label: "Notice / Demand Order ID (Ref ID)", key: "ref_id" },
        { label: "Doc ID", key: "doc_id" },
        { label: "Application ID", key: "appln_id" },
        { label: "Type", key: "type" },
        { label: "Issued By", key: "issued_by" },
      ],
    },
    {
      section: "Description",
      fields: [{ label: "Description", key: "description" }],
    },
    {
      section: "Dates & Amount",
      fields: [
        { label: "Issue Date", key: "issue_date", render: date },
        { label: "Due Date", key: "due_date", render: date },
        { label: "Notice Created", key: "notice_creation_time", render: dateTime },
        { label: "Amount", key: "amount", render: number },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Notice Status", key: "notice_status", render: cleanStatus },
        { label: "Response Status", key: "response_status", render: cleanStatus },
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Client", key: "client_name" },
        { label: "Client ID", key: "client_id" },
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
      ],
    },
    {
      section: "Files",
      fields: [
        {
          label: "Notice Letter URL",
          key: "notice_letter",
          render: pdfPreview,
          span: 3,
        },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const GST_ADDITIONAL_NOTICE_DETAIL = {
  title: "GST Additional Notice",
  backLabel: "GST Additional Notices",
  fieldGroups: [
    {
      section: "Identifiers",
      fields: [
        { label: "Notice / Demand Order ID (Ref ID)", key: "ref_id" },
        { label: "ARN", key: "arn" },
        { label: "Type", key: "type" },
      ],
    },
    {
      section: "Description",
      fields: [{ label: "Description", key: "description" }],
    },
    {
      section: "Dates",
      fields: [
        { label: "Issue Date", key: "issue_date", render: date },
        { label: "Due Date", key: "due_date", render: date },
        {
          label: "Notice Created",
          key: "notice_creation_time",
          render: dateTime,
        },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Notice Status", key: "notice_status", render: cleanStatus },
        { label: "Response Status", key: "response_status", render: cleanStatus },
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Client", key: "client_name" },
        { label: "Client ID", key: "client_id" },
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
        { label: "Case Details ID", key: "case_details_id" },
      ],
    },
    {
      section: "Files",
      fields: [
        {
          label: "Notice Letter URL",
          key: "notice_letter",
          render: pdfPreview,
          span: 3,
        },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const IT_EPROCEEDING_DETAIL = {
  title: "Income Tax E-Proceeding",
  backLabel: "Income Tax E-Proceedings",
  fieldGroups: [
    {
      section: "Identifiers",
      fields: [
        { label: "Notice / Demand Order ID (DIN)", key: "notice_din" },
        { label: "Proceeding Name", key: "proceeding_name" },
        { label: "Proceeding Type", key: "proceeding_type" },
        { label: "Name of Assessee", key: "name_of_assessee" },
        { label: "PAN", key: "pan" },
        {
          label: "Communication Reference ID",
          key: "notice_communication_reference_id",
        },
      ],
    },
    {
      section: "Section / FY / AY",
      fields: [
        { label: "Notice Section", key: "notice_section" },
        { label: "Financial Year", key: "financial_year" },
        { label: "Assessment Year", key: "assessment_year" },
      ],
    },
    {
      section: "Dates",
      fields: [
        {
          label: "Response Due Date",
          key: "response_due_date",
          render: date,
        },
        { label: "Date of Compliance", key: "date_of_compliance" },
        { label: "Notice Sent Date", key: "notice_sent_date", render: date },
        {
          label: "Notice Created",
          key: "notice_creation_time",
          render: dateTime,
        },
        { label: "Last Response Submitted", key: "last_response_submitted" },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Response Status", key: "response_status", render: cleanStatus },
        { label: "Proceeding Status", key: "proceeding_status" },
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
        { label: "FYI Only", key: "fyi", render: yesNo },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Client", key: "client_name" },
        { label: "Client ID", key: "client_id" },
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
      ],
    },
    {
      section: "Files",
      fields: [
        {
          label: "Notice Letter URL",
          key: "notice_letter",
          render: pdfPreview,
          span: 3,
        },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const IT_ROD_DETAIL = {
  title: "Response to Outstanding Demand",
  backLabel: "Response to Outstanding Demands",
  fieldGroups: [
    {
      section: "Identifiers",
      fields: [
        {
          label: "Notice / Demand Order ID (Demand Ref No.)",
          key: "demand_reference_no",
        },
        { label: "Section Code", key: "section_code" },
        { label: "Assessment Year", key: "assessment_year" },
      ],
    },
    {
      section: "Demand & Response",
      fields: [
        {
          label: "Outstanding Demand Amount",
          key: "outstanding_demand_amount",
          render: number,
        },
        { label: "Response Type", key: "response_type" },
        { label: "Mode of Service", key: "mode_of_service" },
        { label: "Rectification Rights", key: "rectification_rights" },
      ],
    },
    {
      section: "Dates",
      fields: [
        {
          label: "Date of Demand Raised",
          key: "date_of_demand_raised",
          render: date,
        },
        {
          label: "Date of Service Notice (Due Date)",
          key: "date_of_service_notice",
          render: date,
        },
        {
          label: "Response Submitted Date",
          key: "response_submitted_date",
          render: date,
        },
        {
          label: "Interest Start Date",
          key: "interest_start_date",
          render: date,
        },
        {
          label: "Notice Created",
          key: "notice_creation_time",
          render: dateTime,
        },
      ],
    },
    {
      section: "Status",
      fields: [
        { label: "Notice Status", key: "notice_status", render: cleanStatus },
        { label: "Response Status", key: "response_status", render: cleanStatus },
        { label: "Disabled", key: "disabled", render: yesNo },
        { label: "Read Only", key: "read_only", render: yesNo },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Client", key: "client_name" },
        { label: "Client ID", key: "client_id" },
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
      ],
    },
    {
      section: "Files",
      fields: [
        {
          label: "Notice Letter URL",
          key: "notice_letter",
          render: pdfPreview,
          span: 3,
        },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};

export const TDS_NOTICE_DETAIL = {
  title: "TDS / TCS Notice",
  backLabel: "TDS / TCS Notices",
  fieldGroups: [
    {
      section: "Identifiers",
      fields: [
        { label: "ID", key: "id" },
        { label: "Financial Year", key: "financial_year" },
      ],
    },
    {
      section: "Demand",
      fields: [
        { label: "Manual Demand", key: "manual_demand", render: number },
        {
          label: "Processed Demand",
          key: "processed_demand",
          render: number,
        },
      ],
    },
    {
      section: "Linkage",
      fields: [
        { label: "Client", key: "client_name" },
        { label: "Client ID", key: "client_id" },
        { label: "Organization", key: "organization_name" },
        { label: "Organization ID", key: "organization_id" },
      ],
    },
    {
      section: "Timestamps",
      fields: [
        {
          label: "Summary Generated At",
          key: "summary_generated_at",
          render: dateTime,
        },
        { label: "Created", key: "created_at", render: dateTime },
        { label: "Updated", key: "updated_at", render: dateTime },
      ],
    },
  ],
};
